import { FC, JSX, useContext, useEffect, useMemo, useRef, useState } from "react";
import { DragAndDropMultiFileUploaderContextProvider } from "@app/shared/DragAndDropMultiFileUploaderContext"
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import Page from "@app/shared/Page";
import PageInner from "@app/shared/PageInner";
import { QESButton } from "@app/shared/qes-standards/Button";
import { WizardNavigationBar } from "@app/shared/qes-standards/WizardNavigationBar";
import { WizardSteps } from "@app/shared/qes-standards/WizardSteps";
import { focusFirstInteractiveElement } from "@app/shared/utility/focusFirstInteractiveElement";
import { CreateExceptionsProvider, CreateExceptionsContext } from "@app/exceptions/contexts/CreateExceptionsContext";
import ExceptionsFiltersContextProvider from "@app/exceptions/contexts/ExceptionsFiltersContext";
import { CreateExceptionsWizardContextSteps } from "@app/exceptions/contexts/interfaces";
import ExceptionsService from "@app/exceptions/exceptionsService";
import { CreateExceptionsTargetModel, CreateSpecialtyGroupExceptions } from "@app/exceptions/interfaces";
import CreateExceptionsAttachment from "@app/exceptions/steps/CreateExceptionsAttachment";
import CreateExceptionsDetails from "@app/exceptions/steps/CreateExceptionsDetails";
import CreateExceptionsProjects from "@app/exceptions/steps/CreateExceptionsProjects";
import CreateExceptionsReview from "@app/exceptions/steps/CreateExceptionsReview";
import CreateExceptionsServiceArea from "@app/exceptions/steps/CreateExceptionsServiceArea";
import CreateExceptionsSpecialties from "@app/exceptions/steps/CreateExceptionsSpecialties";

const CreateExceptionsWizard: FC = () => {
  return (
    <ExceptionsFiltersContextProvider>
      <CreateExceptionsProvider>
        <DragAndDropMultiFileUploaderContextProvider>
          <CreateExceptionsWizardContent />
        </DragAndDropMultiFileUploaderContextProvider>
      </CreateExceptionsProvider>
    </ExceptionsFiltersContextProvider>
  );
}

const CreateExceptionsWizardContent = (): JSX.Element => {
  const wizardContext = useContext(CreateExceptionsContext);
  const wizardBodyRef = useRef<HTMLDivElement>(null);
  const exceptionsService = useRef(new ExceptionsService()).current;

  const [pageIsValid, setPageIsValid] = useState(false);
  const [submitClicked, setSubmitClicked] = useState(false);

  const humanReadablePageNames = {
    [CreateExceptionsWizardContextSteps.ServiceArea]: "Service Area",
    [CreateExceptionsWizardContextSteps.Specialty]: "Specialty",
    [CreateExceptionsWizardContextSteps.Project]: "Project",
    [CreateExceptionsWizardContextSteps.Details]: "Details",
    [CreateExceptionsWizardContextSteps.Attachment]: "Attachment",
    [CreateExceptionsWizardContextSteps.Review]: "Review",
  };

  const getCurrentStep = () => {
    return wizardContext.page;
  }

  const handleUpdatePage = (newPage: number) => {
    const step = Object.entries(CreateExceptionsWizardContextSteps).find((entry) => parseInt(entry[0], 10) === newPage)[1] as CreateExceptionsWizardContextSteps;
    wizardContext.setPage(CreateExceptionsWizardContextSteps[step] as unknown as CreateExceptionsWizardContextSteps);
  };

  const goToPreviousPage = () => {
    switch (wizardContext.page) {
      case CreateExceptionsWizardContextSteps.Specialty:
        wizardContext.setPage(CreateExceptionsWizardContextSteps.ServiceArea);
        wizardContext.setShowChangeWarning(true);
        break;
      case CreateExceptionsWizardContextSteps.Project:
        wizardContext.setPage(CreateExceptionsWizardContextSteps.Specialty);
        wizardContext.setShowChangeWarning(true);
        break;
      case CreateExceptionsWizardContextSteps.Details:
        wizardContext.setPage(CreateExceptionsWizardContextSteps.Project);
        wizardContext.setShowChangeWarning(true);
        break;
      case CreateExceptionsWizardContextSteps.Attachment:
        wizardContext.setPage(CreateExceptionsWizardContextSteps.Details);
        wizardContext.setShowChangeWarning(true);
        break;
      case CreateExceptionsWizardContextSteps.Review:
        wizardContext.setPage(CreateExceptionsWizardContextSteps.Attachment);
        break;
    }
  };

  const validateAndAdvance = () => {
    let newPage: CreateExceptionsWizardContextSteps;
    switch (wizardContext.page) {
      case CreateExceptionsWizardContextSteps.ServiceArea:
        newPage = CreateExceptionsWizardContextSteps.Specialty;
        break;
      case CreateExceptionsWizardContextSteps.Specialty:
        newPage = CreateExceptionsWizardContextSteps.Project;
        break;
      case CreateExceptionsWizardContextSteps.Project:
        newPage = CreateExceptionsWizardContextSteps.Details;
        break;
      case CreateExceptionsWizardContextSteps.Details:
        newPage = CreateExceptionsWizardContextSteps.Attachment;
        break;
      case CreateExceptionsWizardContextSteps.Attachment:
        newPage = CreateExceptionsWizardContextSteps.Review;
        break;
    }
    if (newPage) {
      wizardContext.setPage(newPage);
      wizardContext.setShowChangeWarning(false);
    }
  };

  const handleSubmit = async () => {
    setSubmitClicked(true);
    const regionSetId = wizardContext.selectedServiceArea.split(':')[2];
    const specialtyGroupCode = wizardContext.selectedSpecialty.split(':')[1];

    const targets: CreateExceptionsTargetModel[] = wizardContext.projects
      .filter(project => project.isChecked)
      .map(project => ({
        projectId: project.customProjectId,
        regionSetId: parseInt(regionSetId, 10),
        specialtyGroupCode,
      }));

    const model: CreateSpecialtyGroupExceptions = {
      comment: wizardContext.comment ? wizardContext.comment : "",
      endDate: wizardContext.endDate ? wizardContext.endDate : null,
      exceptionNarrative: wizardContext.narrative ? wizardContext.narrative : "",
      excludeFromCCIIOJustification: wizardContext.excludeFromCCIIOJustification,
      previouslyApproved: wizardContext.previouslyApproved,
      reviewStatus: wizardContext.reviewStatus,
      specialtyGroupExceptionStatus: wizardContext.status,
      startDate: wizardContext.startDate ? wizardContext.startDate : null,
      supplementaryDocuments: wizardContext.attachments ? wizardContext.attachments : [],
      targets,
    };

    const hasAttachments = wizardContext.attachments.length > 0;

    exceptionsService.createSpecialtyGroupExceptions(model)
      .then(() => {
        toast.success("Exceptions have been successfully submitted.", {
          autoClose: 3000,
          containerId: "z-index-9999",
          onClose: () => {
            window.location.pathname = window.location.pathname.replace(/\/create$/, '/view');
          },
        });

        if (hasAttachments) {
          toast.success(`Document${wizardContext.attachments.length > 1 ? 's have' : ' has'} been successfully attached!`, {
            autoClose: 3000,
            containerId: "z-index-9999",
          });
        }
      })
      .catch(() => {
        setSubmitClicked(false);
        toast.error("There was an error processing your request. Please try again.", {
          containerId: "z-index-9999"
        });
      })
  }

  const currentWizardStep = useMemo(() => {
    switch (wizardContext.page) {
      case CreateExceptionsWizardContextSteps.ServiceArea:
        return <CreateExceptionsServiceArea pageIsValid={setPageIsValid} />;
      case CreateExceptionsWizardContextSteps.Specialty:
        return <CreateExceptionsSpecialties pageIsValid={setPageIsValid} />;
      case CreateExceptionsWizardContextSteps.Project:
        return <CreateExceptionsProjects pageIsValid={setPageIsValid} />;
      case CreateExceptionsWizardContextSteps.Details:
        return <CreateExceptionsDetails pageIsValid={setPageIsValid} />;
      case CreateExceptionsWizardContextSteps.Attachment:
        return <CreateExceptionsAttachment pageIsValid={setPageIsValid} />;
      case CreateExceptionsWizardContextSteps.Review:
        return <CreateExceptionsReview pageIsValid={setPageIsValid} />;
    }
  }, [wizardContext.page]);

  useEffect(() => {
    if (!wizardContext.loading) {
      focusFirstInteractiveElement(wizardBodyRef);
    }
  }, [wizardContext.loading]);

  return (
    <Page documentTitle="New Exceptions">
      <PageInner className="create-exceptions-wizard">
        <header className="page-title-bar row justify-content-between align-items-center mt-4 mb-3">
          <h1 className="page-title mt-0 mb-0">New Exceptions</h1>
        </header>

        <WizardSteps currentStep={getCurrentStep()} steps={Object.values(humanReadablePageNames)} setStep={handleUpdatePage} />

        <div ref={wizardBodyRef}>
          {wizardContext.loading ?
            !!wizardContext.error ?
              null :
              <LoadingSpinner /> :
            currentWizardStep}
        </div>
      </PageInner>

      <WizardNavigationBar
        leftSide={(
          <Link to="/exceptions/view" className="qes-button qes-button-secondary danger" data-testid="create-exceptions-wizard__cancel">Cancel</Link>
        )}

        rightSide={(
          <div className="create-exceptions-wizard__nav-buttons">
            {wizardContext.page !== CreateExceptionsWizardContextSteps.ServiceArea &&
              <QESButton qesType="secondary" onClick={goToPreviousPage} qesTestId="create-exceptions-wizard__previous">Previous</QESButton>}
            {wizardContext.page === CreateExceptionsWizardContextSteps.Review ? (
              <QESButton
                disabled={!pageIsValid || submitClicked}
                qesType="primary"
                qesTestId="create-exceptions-wizard__submit"
                className="ms-3"
                onClick={handleSubmit}>
                Submit
              </QESButton>
            ) : (
              <QESButton
                onClick={validateAndAdvance}
                disabled={!pageIsValid}
                qesTestId="create-exceptions-wizard__next"
                className="ms-3">
                Next
              </QESButton>
            )}
          </div>
        )}
      />
    </Page>
  );

};

export default CreateExceptionsWizard;